
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function MonochromeClassicHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-white text-black">
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Bodoni+Moda:opsz,wght@6..96,700&family=Inter:wght@400;500&display=swap');
        .font-display { font-family: 'Bodoni Moda', serif; }
        .font-sans { font-family: 'Inter', sans-serif; }
      `}</style>
      <header className="container mx-auto px-8 py-6 flex justify-between items-center font-sans">
        <h2 className="text-xl font-bold tracking-[0.3em]">ESSENCE</h2>
        <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-widest">
          <a href="#" className="hover:opacity-70">
            Journal
          </a>
          <a href="#" className="hover:opacity-70">
            About
          </a>
          <a href="#" className="hover:opacity-70">
            Contact
          </a>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="md:hidden bg-white/95 border-y">
            <nav className="container mx-auto flex flex-col items-start gap-4 p-6 text-lg uppercase tracking-widest">
                 <a href="#" className="hover:opacity-70">Journal</a>
                 <a href="#" className="hover:opacity-70">About</a>
                 <a href="#" className="hover:opacity-70">Contact</a>
            </nav>
        </div>
      )}

      <main>
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-16">
              <h1 className="font-display text-6xl md:text-8xl">
                The Art of Being
              </h1>
              <p className="font-sans text-lg mt-4 max-w-2xl mx-auto text-gray-600">
                A publication on considered living, design, and culture.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="relative h-[70vh]">
                <Image
                  src="https://picsum.photos/seed/mono1/1000/1200"
                  alt="Black and white photo of a person's silhouette against a bright window"
                  fill
                  className="object-cover"
                  priority
                  data-ai-hint="silhouette window"
                />
              </div>
              <div className="font-sans px-4 md:px-0">
                <p className="text-sm uppercase tracking-widest text-gray-500">
                  Featured Story
                </p>
                <h2 className="font-display text-4xl mt-2">
                  In conversation with a minimalist architect
                </h2>
                <p className="mt-4 text-gray-700 leading-relaxed">
                  We spoke with John Lin about his philosophy of "essentialism"
                  and how it shapes his stark, beautiful, and deeply personal
                  spaces. An exploration of light, shadow, and material.
                </p>
                <Button variant="link" className="p-0 mt-6 text-black group">
                  Read More{' '}
                  <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-50 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h3 className="font-display text-4xl">Latest Articles</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-10 font-sans">
              <div className="group">
                <div className="overflow-hidden mb-4">
                  <Image
                    src="https://picsum.photos/seed/mono2/800/600"
                    alt="A perfectly arranged flat-lay of design tools"
                    width={800}
                    height={600}
                    className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                    data-ai-hint="design tools"
                  />
                </div>
                <p className="text-xs uppercase tracking-widest text-gray-500">
                  Design
                </p>
                <h4 className="font-display text-2xl mt-1">
                  The Enduring Appeal of Bauhaus
                </h4>
              </div>
              <div className="group">
                <div className="overflow-hidden mb-4">
                  <Image
                    src="https://picsum.photos/seed/mono3/800/600"
                    alt="A person sitting alone on a subway, captured in black and white"
                    width={800}
                    height={600}
                    className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                    data-ai-hint="person subway"
                  />
                </div>
                <p className="text-xs uppercase tracking-widest text-gray-500">
                  Culture
                </p>
                <h4 className="font-display text-2xl mt-1">
                  Finding Quiet in a Loud World
                </h4>
              </div>
              <div className="group">
                <div className="overflow-hidden mb-4">
                  <Image
                    src="https://picsum.photos/seed/mono4/800/600"
                    alt="Close up on the texture of a concrete wall"
                    width={800}
                    height={600}
                    className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                    data-ai-hint="concrete texture"
                  />
                </div>
                <p className="text-xs uppercase tracking-widest text-gray-500">
                  Architecture
                </p>
                <h4 className="font-display text-2xl mt-1">
                  The Poetry of Brutalism
                </h4>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="container mx-auto grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-5 text-center">
              <h3 className="font-display text-3xl md:text-4xl">
                "Simplicity is the ultimate sophistication."
              </h3>
              <p className="font-sans mt-4 text-gray-500">
                - Leonardo da Vinci
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="relative h-[60vh]">
                <Image
                  src="https://picsum.photos/seed/mono5/1200/800"
                  alt="A single, bare tree in a misty, black and white landscape"
                  fill
                  className="object-cover"
                  data-ai-hint="bare tree"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-black text-white font-sans py-16 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-display text-3xl">Subscribe to Essence</h3>
              <p className="text-gray-400 mt-2">
                Receive our weekly letter on living with intention.
              </p>
            </div>
            <div className="flex items-center">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full bg-gray-900 border border-gray-700 p-3 placeholder-gray-500 text-white"
              />
              <button className="bg-white text-black p-3 h-full">
                <ArrowRight />
              </button>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-4">
            <p>&copy; {new Date().getFullYear()} Essence Media</p>
            <div className="flex gap-6 uppercase tracking-widest">
              <a href="#" className="hover:text-white">
                Instagram
              </a>
              <a href="#" className="hover:text-white">
                Submissions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
